import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Landmark, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, track } from "../lib/api";
import { useStore } from "../context/StoreContext";

function Row({ label, value }) {
  if (!value) return null;
  const copy = () => { navigator.clipboard?.writeText(String(value)); toast.success(`${label} copied`); };
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-linen/70 last:border-0">
      <span className="text-stonemuted text-sm">{label}</span>
      <button onClick={copy} className="font-mono text-sm tracking-wide flex items-center gap-2 hover:text-champagne-dark" data-testid={`bank-${label.toLowerCase().replace(/\s/g, "-")}`}>
        {value} <Copy className="w-3.5 h-3.5 text-stonemuted" />
      </button>
    </div>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { clearCart } = useStore();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!orderId) { setStatus("error"); return; }
    api.get(`/orders/${orderId}`).then(({ data }) => {
      setOrder(data);
      if (!cleared.current) { clearCart(); cleared.current = true; }
      if (data.payment_status === "paid") { setStatus("paid"); track("purchase_completed", { order_id: orderId }); }
      else if (data.payment_method === "bank_transfer" && data.payment_status === "awaiting_payment") setStatus("bank");
      else if (["cancelled", "failed", "expired"].includes(data.payment_status)) setStatus("failed");
      else setStatus("pending");
    }).catch(() => setStatus("error"));
  }, [orderId]);

  const bank = order?.bank_snapshot || {};

  return (
    <div className="luxy-container py-24 sm:py-32 text-center max-w-xl mx-auto">
      {status === "checking" && (
        <>
          <div className="font-serif text-2xl tracking-[0.3em] text-stonemuted animate-pulse">CONFIRMING</div>
          <p className="text-sm text-stonemuted mt-4">One moment while we retrieve your order.</p>
        </>
      )}

      {status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-emeraldauth/10 border border-emeraldauth/30 flex items-center justify-center mx-auto mb-6">
            <Check className="w-7 h-7 text-emeraldauth" />
          </div>
          <p className="eyebrow mb-3">Order Confirmed</p>
          <h1 className="font-serif text-4xl text-pitch">Thank you</h1>
          <p className="text-charcoal/70 mt-4 leading-relaxed">Your payment has been received and your order is confirmed. A confirmation email is on its way. Your piece will be prepared for insured dispatch.</p>
          {orderId && <p className="font-mono text-sm mt-6 tracking-wider">Order {orderId}</p>}
          <div className="flex gap-4 justify-center mt-10 flex-wrap">
            <Link to="/account" className="bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4">View My Orders</Link>
            <Link to="/shop" className="border border-pitch text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-pitch hover:text-ivory transition-colors">Continue Shopping</Link>
          </div>
        </>
      )}

      {status === "bank" && (
        <div className="text-left" data-testid="bank-instructions">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-champagne/20 border border-champagne-dark/30 flex items-center justify-center mx-auto mb-6">
              <Landmark className="w-7 h-7 text-champagne-dark" />
            </div>
            <p className="eyebrow mb-3">Piece Reserved</p>
            <h1 className="font-serif text-4xl text-pitch">Complete your bank transfer</h1>
            <p className="text-charcoal/70 mt-4 leading-relaxed">Your item is held exclusively for you. Please transfer the total below and quote your reference. We'll confirm your order by email once funds are received.</p>
          </div>

          <div className="bg-bone p-6 mt-8">
            <div className="flex justify-between items-baseline mb-4">
              <span className="eyebrow">Amount Due</span>
              <span className="font-serif text-2xl text-pitch">{formatPrice(order?.total)} AUD</span>
            </div>
            <Row label="Account Name" value={bank.account_name} />
            <Row label="BSB" value={bank.bsb} />
            <Row label="Account Number" value={bank.account_number} />
            <Row label="Reference" value={bank.reference} />
          </div>
          {bank.instructions && <p className="text-sm text-charcoal/70 mt-4 leading-relaxed">{bank.instructions}</p>}
          {(!bank.account_name && !bank.account_number) && (
            <p className="text-sm text-vermilion mt-4">Bank details are being finalised — our team will email you the transfer instructions shortly.</p>
          )}
          <p className="font-mono text-sm mt-6 tracking-wider text-center">Order {orderId}</p>
          <div className="flex gap-4 justify-center mt-8 flex-wrap">
            <Link to="/account" className="bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4">View My Orders</Link>
            <Link to="/shop" className="border border-pitch text-xs font-mono uppercase tracking-[0.2em] px-8 py-4 hover:bg-pitch hover:text-ivory transition-colors">Continue Shopping</Link>
          </div>
        </div>
      )}

      {(status === "failed" || status === "error") && (
        <>
          <p className="eyebrow mb-3 text-vermilion">Payment Not Completed</p>
          <h1 className="font-serif text-4xl text-pitch">Something went wrong</h1>
          <p className="text-charcoal/70 mt-4">Your order could not be confirmed. No charge has been finalised and any reserved piece has been released.</p>
          <Link to="/checkout" className="inline-block mt-8 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4">Return to Checkout</Link>
        </>
      )}

      {status === "pending" && (
        <>
          <p className="eyebrow mb-3">Processing</p>
          <h1 className="font-serif text-4xl text-pitch">Payment processing</h1>
          <p className="text-charcoal/70 mt-4">Your payment is still being confirmed. We'll email you once it completes.</p>
          <Link to="/account" className="inline-block mt-8 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">View My Orders</Link>
        </>
      )}
    </div>
  );
}
